import { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Eye,
    FileText,
    ExternalLink,
    StickyNote,
    Loader2,
    Check,
} from "lucide-react";
import { toast } from "sonner";
import { api, fmtEUR, fmtDate, tradeLabel } from "@/lib/api";

export default function DevisDetailsDialog({ devis, onUpdated }) {
    const [open, setOpen] = useState(false);
    const hasPdf = devis.source === "pdf";
    const pdfUrl = api.devisPdfUrl(devis.id);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    data-testid={`devis-details-btn-${devis.id}`}
                    className="rounded-none border-ink"
                >
                    <Eye className="w-4 h-4 mr-2" strokeWidth={1.6} />
                    Détails
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-5xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                        {devis.contractor_name}
                        <span className="pill border-ink text-ink bg-white">
                            {tradeLabel(devis.trade)}
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-4 gap-3 border-y border-border py-4">
                    <Info label="Référence" value={devis.reference || "—"} />
                    <Info label="Date" value={fmtDate(devis.created_at)} />
                    <Info
                        label="Total HT"
                        value={fmtEUR(devis.total_ht)}
                        mono
                    />
                    <Info
                        label="Total TTC"
                        value={fmtEUR(devis.total_ttc)}
                        mono
                    />
                    {devis.contractor_email && (
                        <Info
                            label="Email"
                            value={devis.contractor_email}
                        />
                    )}
                    {devis.contractor_phone && (
                        <Info
                            label="Téléphone"
                            value={devis.contractor_phone}
                        />
                    )}
                    <Info label="TVA" value={`${devis.tva_rate} %`} mono />
                    <Info
                        label="Source"
                        value={
                            devis.source === "pdf"
                                ? "PDF importé"
                                : devis.source === "public_link"
                                ? "Lien public"
                                : "Saisie manuelle"
                        }
                    />
                </div>

                {hasPdf && (
                    <div className="border border-ink bg-[#FAFAFA] p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <FileText
                                className="w-4 h-4 shrink-0 text-ink-soft"
                                strokeWidth={1.6}
                            />
                            <div className="min-w-0">
                                <div className="text-sm font-medium truncate">
                                    {devis.pdf_filename || "Devis original"}
                                </div>
                                <div className="text-[11px] text-ink-soft">
                                    PDF original archivé sur le chantier
                                </div>
                            </div>
                        </div>
                        <Button
                            data-testid={`open-pdf-btn-${devis.id}`}
                            className="rounded-none bg-ink text-white hover:bg-ink/90 shrink-0"
                            onClick={() => window.open(pdfUrl, "_blank")}
                        >
                            <ExternalLink
                                className="w-4 h-4 mr-2"
                                strokeWidth={1.6}
                            />
                            Ouvrir le PDF
                        </Button>
                    </div>
                )}

                {devis.notes && (
                    <div className="text-xs text-ink-soft border-l-2 border-accent pl-3">
                        {devis.notes}
                    </div>
                )}

                {/* Notes & remarques (modifiable) */}
                <NotesBlock devis={devis} onUpdated={onUpdated} />

                <div>
                    <div className="stat-label mb-2">
                        Postes du devis ({devis.line_items?.length || 0})
                    </div>
                    <div className="border border-border">
                        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#FAFAFA] border-b border-border text-[11px] uppercase tracking-wide text-ink-soft font-medium">
                            <div className="col-span-6">Désignation</div>
                            <div className="col-span-1 text-right">Qté</div>
                            <div className="col-span-1">Unité</div>
                            <div className="col-span-2 text-right">PU HT</div>
                            <div className="col-span-2 text-right">
                                Total HT
                            </div>
                        </div>
                        {(devis.line_items || []).length === 0 ? (
                            <div className="px-3 py-6 text-sm text-ink-soft text-center">
                                Aucun poste enregistré.
                            </div>
                        ) : (
                            (devis.line_items || []).map((it, idx) => (
                                <div
                                    key={it.id || idx}
                                    className="grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-border last:border-0 items-start hover:bg-[#FAFAFA]"
                                    data-testid={`detail-item-${idx}`}
                                >
                                    <div className="col-span-6">
                                        <div className="text-sm font-medium leading-snug">
                                            {it.description}
                                        </div>
                                        {it.category && (
                                            <div className="text-[10px] uppercase tracking-wide text-ink-soft mt-0.5">
                                                {it.category}
                                            </div>
                                        )}
                                        {it.tags && it.tags.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                {it.tags.slice(0, 6).map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="text-[10px] uppercase tracking-wide bg-white border border-border px-1.5 py-0.5 text-ink-soft"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-1 text-right num text-sm">
                                        {it.quantity}
                                    </div>
                                    <div className="col-span-1 text-sm">
                                        {it.unit || "—"}
                                    </div>
                                    <div className="col-span-2 text-right num text-sm">
                                        {fmtEUR(it.unit_price)}
                                    </div>
                                    <div className="col-span-2 text-right num text-sm font-bold">
                                        {fmtEUR(it.total)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                    >
                        Fermer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Info({ label, value, mono }) {
    return (
        <div>
            <div className="stat-label">{label}</div>
            <div
                className={`text-sm font-medium mt-1 ${
                    mono ? "num" : ""
                } break-words`}
            >
                {value}
            </div>
        </div>
    );
}

function NotesBlock({ devis, onUpdated }) {
    const [value, setValue] = useState(devis.user_notes || "");
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const initial = useRef(devis.user_notes || "");
    const timer = useRef(null);

    useEffect(() => {
        setValue(devis.user_notes || "");
        initial.current = devis.user_notes || "";
    }, [devis.id, devis.user_notes]);

    const save = async (v) => {
        if (v === initial.current) return;
        setSaving(true);
        try {
            await api.updateDevis(devis.id, { user_notes: v });
            initial.current = v;
            setSavedAt(Date.now());
            onUpdated && onUpdated();
        } catch {
            toast.error("Erreur d'enregistrement de la note");
        } finally {
            setSaving(false);
        }
    };

    // Debounced autosave on change
    const onChange = (e) => {
        const v = e.target.value;
        setValue(v);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => save(v), 1500);
    };

    const onBlur = () => {
        if (timer.current) clearTimeout(timer.current);
        save(value);
    };

    return (
        <div
            className="border border-warn/40 bg-warn/5"
            data-testid={`notes-block-${devis.id}`}
        >
            <div className="px-3 py-2 border-b border-warn/30 flex items-center justify-between bg-warn/10">
                <div className="flex items-center gap-2">
                    <StickyNote
                        className="w-4 h-4 text-warn"
                        strokeWidth={1.8}
                    />
                    <span className="stat-label text-ink">
                        Mes notes sur ce devis
                    </span>
                </div>
                <div className="text-[10px] uppercase tracking-wide text-ink-soft inline-flex items-center gap-1">
                    {saving ? (
                        <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Enregistrement…
                        </>
                    ) : savedAt ? (
                        <>
                            <Check
                                className="w-3 h-3 text-ok"
                                strokeWidth={2.5}
                            />
                            Enregistré
                        </>
                    ) : (
                        "Sauvegarde automatique"
                    )}
                </div>
            </div>
            <Textarea
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                rows={4}
                placeholder="Questions, doublons repérés, points à challenger, postes flous, demandes de précision à faire à l'artisan…"
                className="rounded-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 resize-y text-sm"
                data-testid={`notes-textarea-${devis.id}`}
            />
        </div>
    );
}
